import { Button, InputLabel, TextField } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { Box } from '@mui/system'
import { useSnackbar } from 'notistack'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import UseTranslation from '../../hooks/UseTranslation'

const OfoqTextField = ({
  name,
  defaultValue,
  refState,
  required,
  type,
  hideBtns,
  customPlaceHolder,
  customStyle,
  ...props
}) => {
  const placeHolder = name?.split('_').join(' ')
  const { t } = UseTranslation()
  const textFieldRef = useRef()
  const [notvalidField, setNotValidField] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    textFieldRef.current.value = defaultValue || ''
    if (defaultValue) {
      updateRefState()
    }
  }, [defaultValue])

  const regexText = /(?!^$)([^\s])/
  const regexPassword = /^\S+$/
  const regexNumber = /^([0-9\-+]){1,11}$/i
  const regexPhone = /^\d\d{10}$/g
  const regexMail = /\S+@\S+\.\w{2,}/
  const regexUrl =
    /^https?:\/\/(?:www\.)?[-a-zA-Z\d@:%._+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b[-a-zA-Z\d()@:%_+.~#?&/=]*$/g

  useEffect(() => {
    if (type === 'phone' || type === 'number') {
      textFieldRef.current.addEventListener('keypress', (evt) => {
        if (evt.which === 8) {
          return
        }
        if (evt.which < 48 || evt.which > 57) {
          evt.preventDefault()
        }
      })
    }
  }, [type])

  const updateRefState = () => {
    const testFields = (condition) => {
      if (condition.test(textFieldRef.current.value)) {
        refState(textFieldRef.current.value)
        setNotValidField(false)
      } else {
        refState('')
        setNotValidField(true)
      }
    }

    if (required) {
      switch (type) {
        case 'text':
          testFields(regexText)
          break

        case 'password':
          testFields(regexPassword)
          break

        case 'number':
          testFields(regexNumber)
          break

        case 'phone':
          testFields(regexPhone)
          break

        case 'email':
          testFields(regexMail)
          break

        case 'url':
          testFields(regexUrl)
          break

        default:
          enqueueSnackbar('wrong field type', { variant: 'error' })
          break
      }
    } else {
      refState(textFieldRef.current.value)
    }
  }

  const toggleNumberField = (value) => {
    refState(Math.max(+defaultValue + value))
    textFieldRef.current.focus()
  }

  return (
    <Box sx={customStyle && customStyle}>
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}>
        {placeHolder}
        {required && <TeamSnap class='snapIcon' />}
      </InputLabel>

      <Box sx={styles.fieldContainer}>
        <TextField
          id={name}
          sx={[styles.formField, notvalidField && styles.formFieldError]}
          placeholder={customPlaceHolder || `${t?.type} ${placeHolder}`}
          inputRef={textFieldRef}
          onChange={updateRefState}
          helperText={
            type === 'email'
              ? t?.['Please add an email with the following format example@example.com']
              : `${t?.['please enter']} ${placeHolder}`
          }
          type={type}
          {...props}
        />

        {type === 'number' && !hideBtns && (
          <Box sx={styles.numberFieldBtns}>
            <Button onClick={() => toggleNumberField(1)}>
              <AddIcon />
            </Button>
            <Button onClick={() => toggleNumberField(-1)}>
              <RemoveIcon />
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
  },

  formField: {
    display: 'flex',
    width: '100%',
    margin: '10px 0 0 0',
    transition: 'all 0.5s ease',
    input: {
      padding: { xs: '10px', sm: '15px' },
      fontSize: { xs: '14px', sm: '17px' },
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
      color: 'error.main',
      margin: '0',
      marginTop: '5px',
    },
    '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input': {
      '-moz-appearance': 'textfield',
      height: { xs: '20px', sm: '1em', md: 'unset' },
      fontSize: { xs: '12px', sm: '14px' },
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fafbfc',
      overflow: 'hidden',
      borderRadius: '15px',

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '15px',
      border: 'solid 1px #e2e8ee',
      transition: 'all 0.3s ease-in-out',
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: '12px',
    },
    '& .MuiAlert-root': {
      width: '100%',
    },
    '& input::placeholder': {
      textTransform: 'capitalize',
      fontSize: { xs: '12px', sm: '14px' },
    },
  },

  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'common.black',
    fontSize: { xs: '12px', md: '16px' },
    width: 'max-content',
    marginBottom: { xs: '10px', sm: '5px' },
    paddingRight: { xs: '14px', sm: '18px' },
    '& svg': {
      mx: '10px',
    },
  },

  formInputLabelRequired: {
    '&:after': {
      content: '"*"',
      color: 'common.white',
      top: '3.4px',
      right: '1.5px',
      position: 'absolute',
      lineHeight: 1.2,
      fontFamily: 'serif',
      fontSize: { xs: '13px', sm: '17px' },
      fontWeight: '900',
    },
    '&:before': {
      content: '" "',
      position: 'absolute',
      width: { xs: '10px', sm: '12px' },
      height: { xs: '10px', sm: '12px' },
      backgroundColor: 'primary.main',
      borderRadius: '50%',
      display: 'inline-block',
      textAlign: 'center',
      verticalAlign: 'middle',
      top: '5px',
      right: '0',
    },
  },

  formFieldError: {
    '& .MuiFormHelperText-root': {
      display: 'flex',
    },
  },

  fieldContainer: {
    position: 'relative',
  },

  numberFieldBtns: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: 0,
    top: '0',

    '& button:nth-of-type(1)': {
      borderTopRightRadius: '5px',
    },

    '& button:nth-of-type(2)': {
      borderBottomRightRadius: '5px',
    },

    '& button': {
      minWidth: { xs: '20px', sm: '30px' },
      maxHeight: { xs: '20px', sm: '28px' },
      backgroundColor: 'primary.main',
      borderRadius: 0,

      '& svg': {
        fontSize: { xs: '12px', sm: '16px' },
        color: 'white',
      },

      '&:hover': {
        backgroundColor: 'primary.main',
        '& svg': {
          color: 'white',
        },
      },
    },
  },
}

export default OfoqTextField
